/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { AIGeneratedSong } from 'services/api/entities';
import {
  BaseSongResponse,
  DeleteProjectResponse,
  ErrorResponse,
  RemixerProjectResponse,
  StudioSongListResponse,
} from 'services/api/responses/responseTypes';
import type { RootState } from 'services/store';
import {
  selectCurrentProjectId,
  selectCurrentSong,
  selectEffectsState,
  selectHistory,
  selectHistoryPointer,
  selectInitialSong,
  selectProjectTitle,
  selectSongs,
} from 'services/store/remixer/selectors';
import {
  ExtraFormulaFields,
  type ExtendedFormulaField,
} from 'services/store/remixer/types';
import { fetchAndCombineSongsData } from 'services/store/helpers/fetchAndCombineSongsData';
import { Song } from 'services/store/songs/types';
import type { QueryReturnValue } from '../base';
import { baseApi } from '../base';
import { TagType } from '../base/baseApi';

import { transformToPaginatedList } from '../songs/transformers';
import {
  StudioProjectsResponseType,
  TranformedRemixerProjectResponse,
  transformProjectResponse,
  transformProjectsResponse,
  transformSongResponse,
} from './transformers';

const remixerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    saveProject: build.mutation<TranformedRemixerProjectResponse, void>({
      queryFn: async (_args, { getState }, _extraOptions, baseFetch) => {
        const state = getState() as RootState;

        const initialSong = selectInitialSong(state);

        const { id: songId } = initialSong?.data || {};
        const title = selectProjectTitle(state);

        if (!songId || !title) {
          throw new Error('Invalid song data.');
        }

        const bodyFormData = new FormData();
        bodyFormData.append('title', title);
        bodyFormData.append('song_id', songId);

        const projectId = selectCurrentProjectId(state);

        if (projectId !== null) {
          bodyFormData.append('id', projectId);
          bodyFormData.append('_method', 'PATCH');
        }

        const historyPointer = selectHistoryPointer(state);
        bodyFormData.append('history_pointer', historyPointer.toString());

        const songs = selectSongs(state);
        const history = selectHistory(state);
        bodyFormData.append(
          'history',
          JSON.stringify(
            history.map(({ formula, songRequestId }) => ({
              formula,
              parentSongId: songs[songRequestId].data?.id,
            }))
          )
        );

        const effects = selectEffectsState(state);
        bodyFormData.append('effects', JSON.stringify(effects));

        const response = (await baseFetch({
          method: 'POST',
          url:
            projectId === null
              ? `/studio/projects`
              : `/studio/projects/${projectId}`,
          body: bodyFormData,
        })) as QueryReturnValue<RemixerProjectResponse, ErrorResponse>;

        const { error, data } = response;

        if (error) {
          const { error_description, payload } = error.data;
          throw new Error(
            `${error_description}, failed service: ${payload?.service}`
          );
        }

        if (!data) {
          throw new Error('Create project endpoint returned no data');
        }

        return { data: transformProjectResponse(data) };
      },
      invalidatesTags: [TagType.PROJECTS, TagType.STUDIO_SONGS],
    }),
    getProject: build.query<TranformedRemixerProjectResponse, string>({
      query: (id) => ({
        url: `/studio/projects/${id}`,
      }),
      transformResponse: transformProjectResponse,
    }),
    getProjects: build.query<StudioProjectsResponseType, number | void>({
      query: (page = 1) => ({
        url: `/studio/projects?page=${page}`,
      }),
      providesTags: [TagType.PROJECTS],
      serializeQueryArgs: ({ endpointName }) => endpointName,

      merge: (currentCache, newItems) => {
        const cache = currentCache;
        cache.studioProjects.push(...newItems.studioProjects);
        cache.isLastPage = newItems.isLastPage;
      },

      forceRefetch: ({ currentArg = 1, previousArg = 1 }) =>
        currentArg > previousArg,
      transformResponse: transformProjectsResponse,
    }),
    deleteProject: build.mutation<DeleteProjectResponse, string>({
      query: (projectId: string) => ({
        url: `/studio/projects/${projectId}`,
        method: 'DELETE',
        body: projectId,
      }),
      async onQueryStarted(projectId, { dispatch }) {
        const updateGetProjectsCache = remixerApi.util.updateQueryData(
          'getProjects',
          undefined,
          (draft) => {
            const projectIndex = draft.studioProjects.findIndex(
              (project) => project.id === projectId
            );
            if (projectIndex >= 0) {
              draft.studioProjects.splice(projectIndex, 1);
            }
            return draft;
          }
        );

        dispatch(updateGetProjectsCache);
      },
    }),
    remixSong: build.mutation<AIGeneratedSong, ExtendedFormulaField>({
      queryFn: async (
        { field, value },
        { getState },
        _extraOptions,
        baseFetch
      ) => {
        const state = getState() as RootState;
        const {
          remixer: {
            songs,
            initialSongRequestId,
            project: { history, historyPointer },
          },
        } = state;

        const parentSongRequestId =
          history[historyPointer - 1]?.songRequestId || initialSongRequestId;

        if (!parentSongRequestId) {
          throw new Error('Can not find parent song Request ID');
        }

        const parentSongId = songs[parentSongRequestId].data?.id;

        if (!parentSongId) {
          throw new Error('Can not find parent song ID');
        }

        const bodyFormData = new FormData();

        if (field === ExtraFormulaFields.UserLoop) {
          bodyFormData.append('user_audio_id', value.loopId);
          bodyFormData.append('entropy', value.entropy.toString());
        } else if (field === ExtraFormulaFields.RemoveUserLoop) {
          bodyFormData.append('remove_user_audio_id', value.loopId);
        } else {
          bodyFormData.append(field, value?.toString() || '');
        }

        const response = (await baseFetch({
          method: 'POST',
          url: `/ai/vega/songs/${parentSongId}/remix`,
          body: bodyFormData,
        })) as QueryReturnValue<BaseSongResponse, ErrorResponse>;

        const { error, data } = response;

        if (error) {
          const { error_description, payload } = error.data;
          throw new Error(
            `${error_description}, failed service: ${payload?.service}`
          );
        }

        if (!data) {
          throw new Error('Remix endpoint returned no data');
        }

        return transformSongResponse(data);
      },
    }),
    getStudioSongs: build.query<
      {
        songs: Record<string, Song>;
        areAllPagesLoaded: boolean;
      },
      number | void
    >({
      queryFn: async (page, _queryApi, _extraOptions, baseFetch) => {
        const result = (await baseFetch(
          `/studio/projects/songs?page=${page}`
        )) as QueryReturnValue<StudioSongListResponse, ErrorResponse>;
        const { data, error } = result;

        if (error) {
          return {
            error: result.error as FetchBaseQueryError,
          };
        }

        const items = transformToPaginatedList(data);
        const songsWithPeaks = await fetchAndCombineSongsData(items.songs);

        return {
          data: {
            ...items,
            songs: songsWithPeaks,
          },
        };
      },
      providesTags: [TagType.STUDIO_SONGS],
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems) => {
        const cache = currentCache;
        Object.entries(newItems.songs).forEach((entry) => {
          const id = entry[0];
          const song = entry[1];
          cache.songs[id] = song;
        });
        cache.areAllPagesLoaded = newItems.areAllPagesLoaded;
      },
      forceRefetch: ({ currentArg = 1, previousArg = 1 }) =>
        currentArg > previousArg,
    }),
    deleteSong: build.mutation<void, string>({
      query: (id: string) => ({
        url: `/studio/projects/songs/${id}`,
        method: 'DELETE',
        body: id,
      }),
      async onQueryStarted(id, { dispatch }) {
        const updateGetSongsCache = () =>
          remixerApi.util.updateQueryData(
            'getStudioSongs',
            undefined,
            (draft) => {
              if (!draft.songs) {
                return;
              }
              draft.songs = Object.fromEntries(
                Object.entries(draft.songs).filter(([key]) => key !== id)
              );
              return draft;
            }
          );

        dispatch(updateGetSongsCache());
      },
    }),
    uploadSong: build.mutation<AIGeneratedSong, Blob>({
      queryFn: async (blob, { getState }, _extraOptions, baseFetch) => {
        const bodyFormData = new FormData();

        const state = getState() as RootState;

        const projectId = selectCurrentProjectId(state);
        if (!projectId) {
          throw new Error('project has no id');
        }

        const title = selectProjectTitle(state);
        if (!title) {
          throw new Error('project has no title');
        }

        const currentSong = selectCurrentSong(state);
        const songId = currentSong?.data?.id;
        if (!songId) {
          throw new Error('current song id is undefined');
        }
        bodyFormData.append('id', projectId);
        bodyFormData.append('title', title);
        bodyFormData.append('music_file', blob);

        const effects = selectEffectsState(state);
        bodyFormData.append('effects', JSON.stringify(effects));

        const response = (await baseFetch({
          method: 'POST',
          url: `/studio/projects/${projectId}/songs`,
          body: bodyFormData,
        })) as QueryReturnValue<BaseSongResponse, ErrorResponse>;

        const { error, data } = response;

        if (error) {
          const { error_description, payload } = error.data;
          throw new Error(
            `${error_description}, failed service: ${payload?.service}`
          );
        }

        if (!data) {
          throw new Error('Upload song endpoint returned no data');
        }

        return transformSongResponse(data);
      },
      invalidatesTags: [TagType.STUDIO_SONGS],
    }),
  }),
  overrideExisting: false,
});

export default remixerApi;
